'use client';

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { DownloadSimple } from '@phosphor-icons/react';
import { VisualizationWrapper } from './visualization-wrapper';
import { useVisualizationContext } from './context';
import { Button } from '@/components/ui/button';
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';
import { resolveSvgBackgroundColor, type SvgBackground } from '@/lib/svg-streaming';
import { cn } from '@/lib/utils';

/**
 * Download the raw SVG as a .svg file — native vector, infinite zoom, editable in any vector tool.
 * This is the highest-fidelity export for SVG (beats raster PNG or rasterized PDF).
 */
function downloadSvgFile(svg: string, title?: string) {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (title || 'visualization')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'visualization';
  a.href = url;
  a.download = `${safeName}.svg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DownloadSvgButton({ svg, title }: { svg: string; title?: string }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => downloadSvgFile(svg, title)}
      className="shrink-0 h-7 sm:h-8 px-1.5 sm:px-2"
      title="Download as .svg — native vector, any size"
    >
      <DownloadSimple weight="bold" className="size-3.5 sm:size-4" />
      <span className="ml-1 text-xs hidden md:inline">SVG</span>
    </Button>
  );
}

interface SvgRendererProps {
  element: {
    props: {
      title?: string;
      svg: string;
      css?: string;
      background?: SvgBackground;
      fit?: 'auto' | 'contain';
      minHeight?: number;
      maxHeight?: number;
      renderActions?: ReactNode;
      displayContext?: 'inline' | 'artifact';
    };
    visualizationId?: string;
  };
}

export function SvgRenderer({ element }: SvgRendererProps) {
  useProductTelemetryOnce(
    'visualization_render_failed',
    !element?.props?.svg,
    {
      tool_name: 'svg',
      failure_kind: 'missing-content',
      visualization_id: element?.visualizationId,
    },
    element?.visualizationId ?? 'missing-svg-content',
  );

  if (!element?.props?.svg) {
    return <div className="p-4 border rounded-lg bg-muted/30 text-muted-foreground">No SVG content provided</div>;
  }

  const { title, svg, css, background, fit, minHeight, maxHeight, renderActions, displayContext = 'inline' } = element.props;
  const resolvedMinHeight = minHeight ?? 260;
  const bgColor = resolveSvgBackgroundColor(background);
  const constrainedStyle = maxHeight ? `max-height: ${maxHeight}px; overflow: auto;` : '';
  const renderStartRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const readyKey = useMemo(
    () => `${element.visualizationId ?? title ?? 'svg'}:${displayContext}`,
    [displayContext, element.visualizationId, title],
  );

  useProductTelemetryOnce(
    'visualization_render_ready',
    Boolean(element?.props?.svg),
    {
      tool_name: 'svg',
      visualization_id: element.visualizationId,
      display_context: displayContext,
      render_duration_ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - renderStartRef.current),
    },
    readyKey,
  );
  const markup = `
    <style>
      .svg-inline-shell {
        width: 100%;
        ${constrainedStyle}
      }

      .svg-inline-shell svg {
        display: block;
        width: 100%;
        height: auto;
        max-width: 100%;
      }

      ${css || ''}
    </style>
    <div class="svg-inline-shell">${svg}</div>
  `.trim();

  return (
    <VisualizationWrapper
      title={undefined}
      minHeight={resolvedMinHeight + 56}
      flexHeight
      headerMode="outside"
      visualizationId={element.visualizationId}
      exportData={{ type: 'svg', content: { title, svg, css, background, minHeight, maxHeight } }}
      renderActions={
        <>
          <DownloadSvgButton svg={svg} title={title} />
          {renderActions}
        </>
      }
      allowFitToggle
      initialViewMode={fit === 'contain' ? 'fit' : 'scroll'}
    >
      <SvgContent markup={markup} bgColor={bgColor} />
    </VisualizationWrapper>
  );
}

function SvgContent({ markup, bgColor }: { markup: string; bgColor: string }) {
  const { viewMode } = useVisualizationContext();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);

  useEffect(() => {
    if (viewMode !== 'fit') {
      setFitScale(1);
      return;
    }
    const compute = () => {
      const wrapper = wrapperRef.current;
      const content = contentRef.current;
      if (!wrapper || !content) return;
      const availableH = wrapper.clientHeight;
      const contentH = content.scrollHeight;
      if (contentH > 0 && availableH > 0) {
        setFitScale(Math.min(availableH / contentH, 1));
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [viewMode, markup]);

  return (
    <div
      ref={wrapperRef}
      className={cn('w-full px-2 pb-2', viewMode === 'fit' && 'h-full overflow-hidden flex justify-center')}
    >
      <div
        ref={contentRef}
        className="w-full rounded-md"
        style={{
          backgroundColor: bgColor,
          ...(viewMode === 'fit' ? {
            transform: `scale(${fitScale})`,
            transformOrigin: 'top center',
          } : undefined),
        }}
        dangerouslySetInnerHTML={{ __html: markup }}
      />
    </div>
  );
}
